(() => {
  const STORAGE_KEY = 'dawn_wishlist_v2';

  const state = {
    items: [],
    panelOpen: false,
    panel: null,
    panelItems: null,
    panelEmpty: null,
    panelCount: null,
    headerCount: null,

    toast: null,
    toastTimer: null,
  };

  const escapeHTML = (str = '') =>
    String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

  const getStorage = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const saveStorage = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
  };

  const findItemIndex = (productId) =>
    state.items.findIndex(
      (item) => String(item.productId) === String(productId)
    );

  const isSaved = (productId) => findItemIndex(productId) !== -1;

  const formatWishlistDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);

    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  const updateCounts = () => {
    const count = state.items.length;

    if (state.headerCount) {
      state.headerCount.textContent = count;
      state.headerCount.hidden = count === 0;
    }

    if (state.panelCount) {
      state.panelCount.textContent = count;
    }
  };

  const syncDOMRefs = () => {
    state.panel = document.querySelector('[data-wishlist-panel]');
    state.panelItems = document.querySelector('[data-wishlist-items]');
    state.panelEmpty = document.querySelector('[data-wishlist-empty]');
    state.panelCount = document.querySelector('[data-wishlist-panel-count]');
    state.headerCount = document.querySelector('[data-wishlist-count]');

    state.toast = document.querySelector('[data-wishlist-toast]');

    // IMPORTANT:
    // move toast outside hidden panel
    if (state.panel && state.toast && state.panel.contains(state.toast)) {
      document.body.appendChild(state.toast);
    }
  };

  const syncButtons = () => {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const active = isSaved(button.dataset.productId);

      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute(
        'aria-label',
        active ? 'Remove from wishlist' : 'Add to wishlist'
      );

      const textEl = button.querySelector('[data-wishlist-text]');
      if (textEl) {
        textEl.textContent = active
          ? 'Remove from wishlist'
          : 'Add to wishlist';
      }
    });
  };

  const openPanel = () => {
    if (!state.panel) return;
    state.panel.hidden = false;
    document.body.style.overflow = 'hidden';
    state.panelOpen = true;
  };

  const closePanel = () => {
    if (!state.panel) return;
    state.panel.hidden = true;
    document.body.style.overflow = '';
    state.panelOpen = false;
  };

  const hideToast = () => {
      if (!state.toast) return;

      state.toast.hidden = true;

      clearTimeout(state.toastTimer);
      state.toastTimer = null;
  };

  const showToast = (item) => {
    if (!state.toast) return;

    clearTimeout(state.toastTimer);

    const titleEl = state.toast.querySelector('[data-wishlist-toast-title]');
    const productEl = state.toast.querySelector('[data-wishlist-toast-product]');

    if (titleEl) {
      titleEl.textContent = 'Product added to wishlist successfully';
    }

    if (productEl) {
      productEl.innerHTML = `
        <div class="wishlist-toast__body">
          ${
            item.image
              ? `<img src="${escapeHTML(item.image)}" alt="${escapeHTML(
                  item.title
                )}" loading="lazy">`
              : ''
          }
          <div>
            <p class="wishlist-toast__product-name">
              ${escapeHTML(item.title)}
            </p>
          </div>
        </div>
      `;
    }

    state.toast.hidden = false;

    state.toastTimer = setTimeout(() => {
      hideToast();
    }, 5000);
  };

  const renderPanel = () => {
    if (!state.panelItems || !state.panelEmpty) return;

    if (!state.items.length) {
      state.panelEmpty.hidden = false;
      state.panelItems.innerHTML = '';
      updateCounts();
      syncButtons();
      return;
    }

    state.panelEmpty.hidden = true;

    state.panelItems.innerHTML = state.items
      .map((item) => {
        const imageHtml = item.image
          ? `<img src="${escapeHTML(
              item.image
            )}" alt="${escapeHTML(item.title)}" loading="lazy">`
          : '';

        const addedDate = formatWishlistDate(item.addedAt);

        return `
          <article class="wishlist-item" data-wishlist-product-id="${escapeHTML(
            item.productId
          )}">
            <a class="wishlist-item__media" href="${escapeHTML(item.url)}">
              ${imageHtml}
            </a>

            <div class="wishlist-item__content">
              <h3 class="wishlist-item__title">
                <a href="${escapeHTML(item.url)}">${escapeHTML(
          item.title
        )}</a>
              </h3>

              ${
                addedDate
                  ? `<p class="wishlist-item__date">saved on ${addedDate}</p>`
                  : ''
              }

              <p class="wishlist-item__meta">${escapeHTML(
                item.priceText || ''
              )}</p>

              <div class="wishlist-item__actions">
                <button
                  type="submit"
                  class="wishlist-item__cart button button--primary"
                  data-wishlist-native-cart
                >
                  <span>Add to cart</span>
                  <div class="loading__spinner hidden">
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      class="spinner"
                      viewBox="0 0 66 66"
                    >
                      <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                    </svg>
                  </div>
                </button>

                <form
                  class="wishlist-native-cart-form"
                  data-type="add-to-cart-form"
                >
                  <input type="hidden" name="id" value="${escapeHTML(item.variantId)}">
                </form>

                <button
                  type="button"
                  class="wishlist-item__remove"
                  data-wishlist-remove="${escapeHTML(item.productId)}"
                  aria-label="Remove item"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8">
                    <path d="M4 7h16"/>
                    <path d="M10 11v6"/>
                    <path d="M14 11v6"/>
                    <path d="M6 7l1 12h10l1-12"/>
                    <path d="M9 7V5h6v2"/>
                  </svg>
                </button>
              </div>
            </div>
          </article>
        `;
      })
      .join('');

    updateCounts();
    syncButtons();
  };

  const toggleItem = (button) => {
    const item = {
      productId: String(button.dataset.productId || ''),
      handle: button.dataset.productHandle || '',
      title: button.dataset.productTitle || '',
      url: button.dataset.productUrl || '',
      image: button.dataset.productImage || '',
      priceText: button.dataset.productPriceText || '',
      variantId: button.dataset.productVariantId || '',
      addedAt: new Date().toISOString(),
    };

    if (!item.productId) return;

    const index = findItemIndex(item.productId);

    if (index === -1) {
      state.items.unshift(item);
      saveStorage();
      renderPanel();

      showToast(item);
    } else {
      state.items.splice(index, 1);
      saveStorage();
      renderPanel();

      hideToast();
    }
  };

  const removeItem = (productId) => {
    const index = findItemIndex(productId);
    if (index === -1) return;

    const confirmed = window.confirm(
      'Are you sure you want to remove this product from wishlist?'
    );

    if (!confirmed) return;

    state.items.splice(index, 1);
    saveStorage();
    renderPanel();
  };

  const clearAll = () => {
    if (!state.items.length) return;

    const confirmed = window.confirm(
      'Remove all products from wishlist?'
    );

    if (!confirmed) return;

    state.items = [];
    saveStorage();
    renderPanel();
  };

  const bindEvents = () => {
    document.addEventListener('click', (event) => {
      const toggleButton = event.target.closest('[data-wishlist-toggle]');
      if (toggleButton) {
        event.preventDefault();
        toggleItem(toggleButton);
        return;
      }

      const openButton = event.target.closest('[data-wishlist-open]');
      if (openButton) {
        event.preventDefault();
        openPanel();
        return;
      }

      const removeButton = event.target.closest('[data-wishlist-remove]');
      if (removeButton) {
        event.preventDefault();
        removeItem(removeButton.dataset.wishlistRemove);
        return;
      }

      const clearButton = event.target.closest('[data-wishlist-clear]');
      if (clearButton) {
        event.preventDefault();
        clearAll();
        return;
      }

      const addCartButton = event.target.closest('[data-wishlist-native-cart]');
      if (addCartButton) {
        event.preventDefault();

        const card = addCartButton.closest('.wishlist-item');
        const form = card?.querySelector('.wishlist-native-cart-form');
        if (!form) return;

        const variantId = form.querySelector('input[name="id"]')?.value;
        if (!variantId) return;

        const submitButton = addCartButton;
        const spinner = submitButton.querySelector('.loading__spinner');

        submitButton.setAttribute('disabled', 'disabled');
        spinner?.classList.remove('hidden');

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);

        const cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        if (cart) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }

        fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          body: formData,
          headers: {
            Accept: 'application/javascript',
          },
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) return;

            cart?.renderContents(response);

            closePanel();
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            submitButton.removeAttribute('disabled');
            spinner?.classList.add('hidden');
          });

        return;
      }

      const closeTrigger = event.target.closest('[data-wishlist-close]');
      if (closeTrigger) {
        event.preventDefault();
        closePanel();
        return;
      }

      const toastClose = event.target.closest('[data-wishlist-toast-close]');
      if (toastClose) {
        event.preventDefault();
        hideToast();
        return;
      }

      const toastView = event.target.closest('[data-wishlist-toast-view]');
      if (toastView) {
        event.preventDefault();
        hideToast();
        openPanel();
        return;
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && state.panelOpen) {
        closePanel();
      }
    });

    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      state.items = getStorage();
      renderPanel();
    });
  };

  const init = () => {
    syncDOMRefs();
    state.items = getStorage();
    syncButtons();
    renderPanel();
    bindEvents();
    updateCounts();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();